import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import clipGenerationApi, { TargetPlatform, IClipGenerationJob } from '@/services/clipGenerationApi';
import { videoApi } from '@/services/videoApi';
import ClipGeneratorWizard from '@/components/Clips/ClipGeneratorWizard';
import { AudioPageLayout } from '@/components/Audio';
import type { SelectedVideo } from '@/components/Clips/VideoLibraryPicker';
import ClipDetailsDrawer, { StatusBadge } from '@/components/Clips/ClipDetailsDrawer';
import Pagination, { paginate } from '@/components/Common/Pagination';

const HISTORY_ITEMS_PER_PAGE = 10;

export const PLATFORMS: { id: TargetPlatform; label: string }[] = [
  { id: 'youtube_shorts', label: 'YouTube Shorts' },
  { id: 'instagram_reels', label: 'Instagram Reels' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'facebook_reels', label: 'Facebook Reels' },
  { id: 'linkedin_video', label: 'LinkedIn Video' },
  { id: 'twitter_video', label: 'Twitter Video' },
];

export const PLATFORM_DEFAULT_COUNTS: Record<TargetPlatform, number> = {
  youtube_shorts: 10,
  instagram_reels: 5,
  tiktok: 3,
  facebook_reels: 5,
  linkedin_video: 2,
  twitter_video: 5,
};

export interface SegmentRow {
  start: string;
  end: string;
  title: string;
}

const parseSegmentRows = (rows: SegmentRow[]) =>
  rows
    .map((r) => ({ start: Number(r.start), end: Number(r.end), title: r.title.trim() || undefined }))
    .filter((r) => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start && r.start >= 0);

const sourceIdentity = (source?: string) => {
  if (!source) return '';
  try {
    const url = new URL(source);
    return `${url.origin}${url.pathname}`;
  } catch {
    return source;
  }
};

export default function ClipGeneratorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedVideo, setSelectedVideo] = useState<SelectedVideo | null>(null);
  const [title, setTitle] = useState('');
  const [transcript, setTranscript] = useState('');
  const [language, setLanguage] = useState('en');
  // Set when the selected video is a translated/dubbed output — its language is
  // fixed to the translation job's target language rather than user-selectable.
  const [lockedLanguage, setLockedLanguage] = useState<string | null>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<TargetPlatform[]>(['youtube_shorts']);
  const [keywords, setKeywords] = useState('');
  const [options, setOptions] = useState({
    faceTracking: true,
    autoZoom: true,
    autoSubtitles: true,
    animatedCaptions: false,
    noiseReduction: true,
    musicRecommendation: true,
    emotionDetection: true,
  });
  const [maxClipLength, setMaxClipLength] = useState<number>(60);
  const [minClipLength, setMinClipLength] = useState<number>(10);
  const [silenceThresholdDb, setSilenceThresholdDb] = useState<number>(-40);
  const [outputCounts, setOutputCounts] = useState<Record<TargetPlatform, number>>({ ...PLATFORM_DEFAULT_COUNTS });
  // Manual clip segments, keyed by scope: 'all' = shared list used by any platform
  // without its own override; a platform id = that platform's override list.
  const [segmentScope, setSegmentScope] = useState<'all' | TargetPlatform>('all');
  const [segmentsByScope, setSegmentsByScope] = useState<Record<string, SegmentRow[]>>({});
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<IClipGenerationJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<IClipGenerationJob | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [activeVideoSource, setActiveVideoSource] = useState('');
  const [historyPage, setHistoryPage] = useState(1);

  // Fetches a translation job's target language + transcript and locks the
  // wizard's language field to it. Non-fatal on failure — the video stays
  // selected, just without the auto-filled language/transcript.
  const applyTranslatedVideoInfo = async (jobId: string) => {
    try {
      const res = await videoApi.getJob(jobId);
      const job = res?.job ?? res?.data?.job ?? res;
      if (!job) return;
      const targetLanguage = job.request?.targetLanguage;
      const translatedText = job.result?.translatedText;
      if (targetLanguage) {
        setLanguage(targetLanguage);
        setLockedLanguage(targetLanguage);
      }
      if (translatedText) {
        setTranscript(translatedText);
      }
    } catch (err) {
      // Non-fatal — the video stays selected, just without the auto-filled
      // language/transcript. Logged so this is diagnosable from the console.
      console.error('Failed to load translation job info for clip generation:', err);
    }
  };

  useEffect(() => {
    const qVideo = searchParams.get('videoS3Url') || '';
    const qTitle = searchParams.get('title') || '';
    const qJobId = searchParams.get('jobId') || '';
    if (qVideo) {
      setSelectedVideo({ url: qVideo, name: qTitle || 'Selected video', jobId: qJobId || undefined });
      setActiveVideoSource(qVideo);
    }
    if (qTitle) setTitle(qTitle);
    if (qJobId) applyTranslatedVideoInfo(qJobId);
    if (qVideo) {
      document.getElementById('clip-generation-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchParams]);

  const handleSelectVideo = (item: SelectedVideo) => {
    setSelectedVideo(item);
    if (!title) setTitle(item.name);
    if (item.jobId) {
      applyTranslatedVideoInfo(item.jobId);
    } else {
      setLockedLanguage(null);
    }
  };

  const handleClearVideo = () => {
    setSelectedVideo(null);
    setLockedLanguage(null);
  };

  const handleCancelWizard = () => {
    setSelectedVideo(null);
    setTitle('');
    setTranscript('');
    setLanguage('en');
    setLockedLanguage(null);
    setSelectedPlatforms(['youtube_shorts']);
    setKeywords('');
    setOptions({
      faceTracking: true,
      autoZoom: true,
      autoSubtitles: true,
      animatedCaptions: false,
      noiseReduction: true,
      musicRecommendation: true,
      emotionDetection: true,
    });
    setMaxClipLength(60);
    setMinClipLength(10);
    setSilenceThresholdDb(-40);
    setOutputCounts({ ...PLATFORM_DEFAULT_COUNTS });
    setSegmentScope('all');
    setSegmentsByScope({});
    // Return to wherever this page was navigated from (Dashboard's quick-start,
    // the Videos page's "Generate Clips" action, etc.) rather than staying here.
    navigate(-1);
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const res = await clipGenerationApi.getJobs();
      if (res.data.success) {
        setJobs(res.data.jobs as IClipGenerationJob[]);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load jobs');
    }
  };

  useEffect(() => {
    if (!jobs.some((job) => job.status === 'queued' || job.status === 'processing')) return;
    const interval = setInterval(loadJobs, 3000);
    return () => clearInterval(interval);
  }, [jobs]);

  const togglePlatform = (platform: TargetPlatform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  // Appends (index === null) or replaces (index) a fully-formed row — the
  // ClipDetailsModal always produces a complete {start, end, title} in one
  // shot, unlike the old add-then-patch-three-fields flow this replaced.
  const upsertSegmentRow = (scope: string, index: number | null, row: SegmentRow) => {
    setSegmentsByScope((prev) => {
      const rows = [...(prev[scope] || [])];
      if (index === null) {
        rows.push(row);
      } else {
        rows[index] = row;
      }
      return { ...prev, [scope]: rows };
    });
  };

  const removeSegmentRow = (scope: string, index: number) => {
    setSegmentsByScope((prev) => ({
      ...prev,
      [scope]: (prev[scope] || []).filter((_, i) => i !== index),
    }));
  };

  // Returns whether the job was started, so the wizard can jump back to
  // step 0 only on success (leaving the user's inputs in place after a
  // failed attempt).
  const handleGenerate = async (): Promise<boolean> => {
    if (selectedPlatforms.length === 0) {
      toast.error('Select at least one target platform');
      return false;
    }
    if (!selectedVideo) {
      toast.error('Select a video to generate clips from');
      return false;
    }

    setLoading(true);
    try {
      const manualSegments = parseSegmentRows(segmentsByScope['all'] || []);
      const platformSegments: Partial<Record<TargetPlatform, ReturnType<typeof parseSegmentRows>>> = {};
      for (const platform of selectedPlatforms) {
        const rows = parseSegmentRows(segmentsByScope[platform] || []);
        if (rows.length > 0) platformSegments[platform] = rows;
      }

      const res = await clipGenerationApi.createJob({
        title: title || undefined,
        videoS3Url: selectedVideo.url,
        transcript: transcript || undefined,
        language,
        targetPlatforms: selectedPlatforms,
        manualSegments: manualSegments.length > 0 ? manualSegments : undefined,
        platformSegments: Object.keys(platformSegments).length > 0 ? platformSegments : undefined,
        options: {
          ...options,
          maxClipLength,
          minClipLength,
          silenceThresholdDb,
          outputCounts,
          detectKeywords: keywords
            .split(',')
            .map((k) => k.trim())
            .filter(Boolean),
        },
      });

      if (res.data.success) {
        const newJob = {
          ...res.data.job,
          jobId: res.data.job.jobId || res.data.jobId,
          title: res.data.job.title || title || undefined,
          videoS3Url: res.data.job.videoS3Url || selectedVideo.url,
          highlights: [],
          clips: [],
        } as IClipGenerationJob;
        setActiveVideoSource(selectedVideo.url);
        setJobs((currentJobs) => [newJob, ...currentJobs.filter((job) => job.jobId !== newJob.jobId)]);
        // Open the progress drawer so the user can watch this job run, rather
        // than leaving them on the wizard with no visible feedback.
        openJobDrawer(newJob.jobId);
        toast.success('Clip generation started');
        await loadJobs();
        return true;
      }
      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start clip generation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const viewJob = async (jobId: string) => {
    try {
      const res = await clipGenerationApi.getJob(jobId);
      if (res.data.success) {
        setSelectedJob(res.data.job);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load job details');
    }
  };

  const openJobDrawer = (jobId: string) => {
    viewJob(jobId);
    setDrawerOpen(true);
  };

  const refreshSelectedJob = async () => {
    if (!selectedJob) return;
    await viewJob(selectedJob.jobId);
  };

  const handleDeleteClip = async (clipId: string) => {
    if (!selectedJob) return;
    try {
      await clipGenerationApi.deleteClip(selectedJob.jobId, clipId);
      toast.success('Clip removed');
      await viewJob(selectedJob.jobId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to remove clip');
    }
  };

  const handleCancelJob = async (jobId: string) => {
    try {
      await clipGenerationApi.cancelJob(jobId);
      toast.success('Job cancelled');
      await loadJobs();
      if (selectedJob?.jobId === jobId) await viewJob(jobId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to cancel job');
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await clipGenerationApi.retryJob(jobId);
      toast.success('Retrying job from last completed step');
      await loadJobs();
      if (selectedJob?.jobId === jobId) await viewJob(jobId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to retry job');
    }
  };

  useEffect(() => {
    if (!selectedJob || selectedJob.status === 'completed' || selectedJob.status === 'failed' || selectedJob.status === 'cancelled') {
      return;
    }
    const interval = setInterval(refreshSelectedJob, 3000);
    return () => clearInterval(interval);
  }, [selectedJob]);

  const filteredJobs = useMemo(() => {
    const term = search.trim().toLowerCase();
    const selectedDate = dateFilter ? new Date(dateFilter) : null;
    const nextMinute = selectedDate ? new Date(selectedDate.getTime() + 60000) : null;
    const activeSource = sourceIdentity(activeVideoSource);
    return jobs.filter((job) => {
      const jobTitle = (job.title || job.videoS3Url || '').toLowerCase();
      const matchesSearch = !term || jobTitle.includes(term) || job.jobId.toLowerCase().includes(term);
      const jobDate = new Date(job.createdAt);
      const matchesDate = !selectedDate || (jobDate >= selectedDate && jobDate < nextMinute!);
      const matchesActiveVideo = !activeSource || sourceIdentity(job.videoS3Url || job.audioS3Url) === activeSource;
      return matchesSearch && matchesDate && matchesActiveVideo;
    });
  }, [jobs, search, dateFilter, activeVideoSource]);

  useEffect(() => {
    setHistoryPage(1);
  }, [search, dateFilter]);

  const paginatedJobs = paginate(filteredJobs, historyPage, HISTORY_ITEMS_PER_PAGE);
  const historyTotalPages = Math.ceil(filteredJobs.length / HISTORY_ITEMS_PER_PAGE);

  return (
    <>
      <AudioPageLayout
        title="AI Clip Generator"
        historyMode="drawer"
        formSection={
          <div id="clip-generation-form">
            <ClipGeneratorWizard
              title={title}
              setTitle={setTitle}
              selectedVideo={selectedVideo}
              onSelectVideo={handleSelectVideo}
              onClearVideo={handleClearVideo}
              transcript={transcript}
              setTranscript={setTranscript}
              language={language}
              setLanguage={setLanguage}
              lockedLanguage={lockedLanguage}
              keywords={keywords}
              setKeywords={setKeywords}
              selectedPlatforms={selectedPlatforms}
              togglePlatform={togglePlatform}
              outputCounts={outputCounts}
              setOutputCounts={setOutputCounts}
              minClipLength={minClipLength}
              setMinClipLength={setMinClipLength}
              maxClipLength={maxClipLength}
              setMaxClipLength={setMaxClipLength}
              silenceThresholdDb={silenceThresholdDb}
              setSilenceThresholdDb={setSilenceThresholdDb}
              segmentScope={segmentScope}
              setSegmentScope={setSegmentScope}
              segmentsByScope={segmentsByScope}
              upsertSegmentRow={upsertSegmentRow}
              removeSegmentRow={removeSegmentRow}
              options={options}
              setOptions={setOptions}
              loading={loading}
              onGenerate={handleGenerate}
              onCancel={handleCancelWizard}
            />
          </div>
        }
        historySection={
          <div className="flex flex-col h-full">
            <div className="p-4 space-y-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title..."
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <input
                type="datetime-local"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {filteredJobs.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No matching clips.</p>}
              {paginatedJobs.map((job) => (
                <div
                  key={job.jobId}
                  className={`w-full text-left rounded-lg border p-4 transition-colors cursor-pointer ${
                    selectedJob?.jobId === job.jobId
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                  onClick={() => openJobDrawer(job.jobId)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white text-sm truncate pr-2">
                      {job.title || job.jobId.slice(0, 8)}
                    </span>
                    <div className="flex items-center gap-2">
                      {(job.status === 'queued' || job.status === 'processing') && (
                        <button
                          type="button"
                          title="Cancel job"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelJob(job.jobId);
                          }}
                          className="p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <StopCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                      {job.status === 'failed' && (
                        <button
                          type="button"
                          title="Retry from last completed step"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetryJob(job.jobId);
                          }}
                          className="p-1 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                        >
                          <ArrowPathIcon className="w-4 h-4" />
                        </button>
                      )}
                      <StatusBadge status={job.status} />
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {job.targetPlatforms.map((p) => p.replace(/_/g, ' ')).join(', ')} · {job.progress}%
                  </div>
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                    {new Date(job.createdAt).toLocaleString()} · {job.currentStep}
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={historyPage}
              totalPages={historyTotalPages}
              onPageChange={setHistoryPage}
              totalItems={filteredJobs.length}
              itemsPerPage={HISTORY_ITEMS_PER_PAGE}
              className="border-t border-gray-200 dark:border-gray-700"
            />
          </div>
        }
      />

      {drawerOpen && selectedJob && (
        <ClipDetailsDrawer
          job={selectedJob}
          onClose={() => setDrawerOpen(false)}
          onDeleteClip={handleDeleteClip}
          onRefreshJob={refreshSelectedJob}
        />
      )}
    </>
  );
}
